import React, { useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, Clock, User, Phone, Mail, MessageSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { TIME_SLOTS } from "@/i18n";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDateISO = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const ALL_SERVICES = [
  "terapia_fisica",
  "rehabilitacion",
  "quiropractica",
  "terapias_alternativas",
  "estetica",
  "consulta_general",
];

export const Booking = ({
  sectionId = "booking",
  tKey = "booking",
  services = ALL_SERVICES,
  defaultService = "",
  testidPrefix = "booking",
  variant = "default", // "default" | "alt" | "estetica"
  whatsappNumber = "527712141634",
}) => {
  const { t, lang } = useLang();
  const tt = t[tKey] || t.booking;
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [service, setService] = useState(defaultService || "");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState([]);
  const [confirmed, setConfirmed] = useState(null); // { name, service, dateISO, time }

  useEffect(() => {
    if (!date) {
      setBooked([]);
      return;
    }
    const iso = formatDateISO(date);
    axios
      .get(`${API}/appointments/availability`, { params: { date: iso } })
      .then((res) => setBooked(res.data?.booked || []))
      .catch(() => setBooked([]));
  }, [date]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name || !phone || !service || !date || !time) {
      toast.error(t.booking.errorTitle, { description: t.booking.errorDesc });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name,
        phone,
        email: email || null,
        service,
        date: formatDateISO(date),
        time,
        notes: notes || "",
      };
      await axios.post(`${API}/appointments`, payload);

      const apptIso = formatDateISO(date);
      const apptDate = date.toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
      const serviceLabel = t.booking.services[service] || service;

      const waMsg = encodeURIComponent(
        lang === "es"
          ? `¡Hola ${name}!\n\n` +
            `Soy del equipo de *TERAFIQUIRO Wellness Center* y queremos darte la bienvenida.\n\n` +
            `Tu cita ha quedado registrada con estos datos:\n` +
            `- Servicio: ${serviceLabel}\n` +
            `- Fecha: ${apptDate}\n` +
            `- Hora: ${time}\n` +
            `- Lugar: Calle El Pípila 200, Colonia Centro, Pachuca de Soto, Hidalgo\n\n` +
            `Para que tu sesión sea aún más efectiva, te pedimos por favor:\n` +
            `- Llegar 10 minutos antes para tu valoración inicial.\n` +
            `- Traer estudios previos: radiografías, resonancias o análisis recientes (si los tienes).\n` +
            `- Llevar una lista de los medicamentos que tomes actualmente.\n` +
            `- Vestir ropa cómoda, holgada y de preferencia deportiva.\n` +
            `- Acudir con buen aseo personal e hidratado.\n` +
            `- Si tomas anticoagulantes o tienes alguna condición especial (embarazo, marcapasos, alergias), avísanos antes de la sesión.\n` +
            `- Evita comidas pesadas justo antes de la consulta.\n\n` +
            `Cualquier duda o cambio puedes responder a este mensaje. Estamos para cuidar de tu cuerpo.\n\n` +
            `_Al cuidado de tu cuerpo_ — TERAFIQUIRO`
          : `Hi ${name}!\n\n` +
            `This is the team at *TERAFIQUIRO Wellness Center*, warmly welcoming you.\n\n` +
            `Your appointment is booked:\n` +
            `- Service: ${serviceLabel}\n` +
            `- Date: ${apptDate}\n` +
            `- Time: ${time}\n` +
            `- Location: Calle El Pípila 200, Colonia Centro, Pachuca de Soto, Hidalgo\n\n` +
            `To make your session even better, please:\n` +
            `- Arrive 10 minutes early for your initial assessment.\n` +
            `- Bring any recent studies: X-rays, MRIs or lab work (if any).\n` +
            `- Bring a list of medications you currently take.\n` +
            `- Wear comfortable, loose, sport-style clothing.\n` +
            `- Come well-rested, hydrated, and with good personal hygiene.\n` +
            `- If you take blood thinners or have any special condition (pregnancy, pacemaker, allergies), let us know beforehand.\n` +
            `- Avoid heavy meals just before your visit.\n\n` +
            `Reply to this message anytime if you have questions or need to reschedule. We are here to care for your body.\n\n` +
            `_Caring for your body_ — TERAFIQUIRO`
      );
      const waUrl = `https://wa.me/${whatsappNumber}?text=${waMsg}`;

      toast.success(t.booking.successTitle, {
        description: t.booking.successDesc,
        duration: 8000,
        action: {
          label: t.fab?.whatsapp || "WhatsApp",
          onClick: () => window.open(waUrl, "_blank", "noopener"),
        },
      });
      // Also auto-open WhatsApp shortly after so the patient can confirm
      setTimeout(() => {
        window.open(waUrl, "_blank", "noopener");
      }, 600);

      setName("");
      setPhone("");
      setEmail("");
      setService(defaultService || "");
      setNotes("");
      setTime("");
      setDate(null);
      setConfirmed({
        name: name,
        service,
        dateISO: apptIso,
        time,
      });
    } catch (err) {
      toast.error(t.booking.errorTitle, {
        description: err?.response?.data?.detail || t.booking.errorDesc,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const bgVariant = {
    default: "bg-gradient-to-b from-white via-green-50/40 to-white",
    alt: "bg-gradient-to-b from-white via-emerald-50/40 to-white",
    estetica: "bg-gradient-to-b from-white via-rose-50/30 to-white",
  }[variant];

  const accentVariant = {
    default: { ring: "focus-visible:ring-green-500", btn: "bg-green-600 hover:bg-green-700", soft: "bg-green-100 text-green-700", chip: "bg-green-600 text-white border-green-600", chipHover: "hover:border-green-300 hover:text-green-700", iconColor: "text-green-600", eyebrowText: "text-green-700" },
    alt: { ring: "focus-visible:ring-emerald-500", btn: "bg-emerald-700 hover:bg-emerald-800", soft: "bg-emerald-100 text-emerald-800", chip: "bg-emerald-700 text-white border-emerald-700", chipHover: "hover:border-emerald-300 hover:text-emerald-800", iconColor: "text-emerald-700", eyebrowText: "text-emerald-800" },
    estetica: { ring: "focus-visible:ring-rose-400", btn: "bg-rose-600 hover:bg-rose-700", soft: "bg-rose-100 text-rose-700", chip: "bg-rose-600 text-white border-rose-600", chipHover: "hover:border-rose-300 hover:text-rose-700", iconColor: "text-rose-600", eyebrowText: "text-rose-700" },
  }[variant];

  return (
    <section
      id={sectionId}
      className={`relative py-24 md:py-32 ${bgVariant}`}
      data-testid={`${testidPrefix}-section`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-2">
            <span className={`inline-block text-xs uppercase tracking-[0.25em] font-semibold ${accentVariant.eyebrowText}`}>
              {tt.eyebrow}
            </span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
              {tt.title}
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">{tt.desc}</p>

            <div className="mt-10 space-y-4">
              {[
                { icon: CalendarDays, label: lang === "es" ? "Disponibilidad en tiempo real" : "Real-time availability" },
                { icon: Clock, label: lang === "es" ? "Confirmación rápida por WhatsApp" : "Quick WhatsApp confirmation" },
                { icon: User, label: lang === "es" ? "Atención personalizada" : "Personalized care" },
              ].map((row, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-xl ${accentVariant.soft} flex items-center justify-center`}>
                    <row.icon className="h-5 w-5" />
                  </div>
                  <p className="text-sm font-medium text-slate-700">{row.label}</p>
                </div>
              ))}
            </div>
          </div>

          {confirmed ? (
            <div
              className="lg:col-span-3 bg-white rounded-3xl shadow-xl shadow-slate-900/5 border border-slate-100 p-8 md:p-10 text-center"
              data-testid={`${testidPrefix}-confirmed`}
            >
              <div className={`mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full ${accentVariant.btn} text-white shadow-lg`}>
                <CalendarDays className="h-8 w-8" />
              </div>
              <h3 className="mt-6 font-display text-2xl sm:text-3xl font-bold text-slate-900">
                {lang === "es" ? "¡Cita registrada!" : "Appointment registered!"}
              </h3>
              <p className="mt-3 text-base text-slate-600 leading-relaxed">
                {lang === "es"
                  ? `${confirmed.name.split(" ")[0]}, recibimos tu solicitud para el ${confirmed.dateISO} a las ${confirmed.time} h.`
                  : `${confirmed.name.split(" ")[0]}, we received your request for ${confirmed.dateISO} at ${confirmed.time}.`}
              </p>
              <p className="mt-4 text-base italic text-green-800 leading-relaxed font-display max-w-lg mx-auto">
                {lang === "es"
                  ? "Agradecemos tu visita y será un placer conocerte y servirte profesionalmente en nuestro consultorio."
                  : "Thank you for reaching out. It will be our pleasure to meet you and serve you professionally at our practice."}
              </p>
              <p className="mt-4 text-sm text-slate-500">
                {lang === "es"
                  ? "Revisa tu correo y WhatsApp en los próximos minutos para la confirmación final."
                  : "Check your email and WhatsApp shortly for the final confirmation."}
              </p>
              <button
                type="button"
                onClick={() => setConfirmed(null)}
                className={`mt-7 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-white ${accentVariant.btn}`}
                data-testid={`${testidPrefix}-new-booking`}
              >
                {lang === "es" ? "Agendar otra cita" : "Book another appointment"}
              </button>
              <p className="mt-6 text-[10px] uppercase tracking-[0.28em] font-semibold text-green-700">
                TerafiQuiro · Al cuidado de tu cuerpo
              </p>
            </div>
          ) : (
          <form
            onSubmit={onSubmit}
            className="lg:col-span-3 bg-white rounded-3xl shadow-xl shadow-slate-900/5 border border-slate-100 p-6 md:p-8"
            data-testid={`${testidPrefix}-form`}
          >
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label className="text-sm font-medium text-slate-700">
                  <User className={`inline h-3.5 w-3.5 mr-1 ${accentVariant.iconColor}`} />
                  {t.booking.name}
                </Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t.booking.name}
                  required
                  className={`mt-1.5 bg-slate-50 border-slate-200 ${accentVariant.ring}`}
                  data-testid={`${testidPrefix}-input-name`}
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">
                  <Phone className={`inline h-3.5 w-3.5 mr-1 ${accentVariant.iconColor}`} />
                  {t.booking.phone}
                </Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+52 ..."
                  required
                  className={`mt-1.5 bg-slate-50 border-slate-200 ${accentVariant.ring}`}
                  data-testid={`${testidPrefix}-input-phone`}
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm font-medium text-slate-700">
                  <Mail className={`inline h-3.5 w-3.5 mr-1 ${accentVariant.iconColor}`} />
                  {t.booking.email}
                </Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@ejemplo.com"
                  className={`mt-1.5 bg-slate-50 border-slate-200 ${accentVariant.ring}`}
                  data-testid={`${testidPrefix}-input-email`}
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm font-medium text-slate-700">{t.booking.service}</Label>
                <Select value={service} onValueChange={setService}>
                  <SelectTrigger
                    className="mt-1.5 bg-slate-50 border-slate-200"
                    data-testid={`${testidPrefix}-select-service`}
                  >
                    <SelectValue placeholder={t.booking.selectService} />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`${testidPrefix}-service-opt-${s}`}>
                        {t.booking.services[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-5">
              <div>
                <Label className="text-sm font-medium text-slate-700">
                  <CalendarDays className={`inline h-3.5 w-3.5 mr-1 ${accentVariant.iconColor}`} />
                  {t.booking.date}
                </Label>
                <div className="mt-1.5 rounded-xl border border-slate-200 bg-slate-50 p-2 flex justify-center" data-testid={`${testidPrefix}-calendar-wrap`}>
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => {
                      const day = d.getDay();
                      return d < today || day === 0;
                    }}
                    className="rounded-md"
                  />
                </div>
                {date && (
                  <p className={`mt-2 text-xs font-medium ${accentVariant.eyebrowText}`} data-testid={`${testidPrefix}-selected-date`}>
                    {date.toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { weekday: "long", day: "numeric", month: "long" })}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700">
                  <Clock className={`inline h-3.5 w-3.5 mr-1 ${accentVariant.iconColor}`} />
                  {t.booking.time}
                </Label>
                <div className="mt-1.5 grid grid-cols-3 gap-2" data-testid={`${testidPrefix}-time-grid`}>
                  {TIME_SLOTS.map((slot) => {
                    const disabled = booked.includes(slot) || !date;
                    const selected = time === slot;
                    const slotClass = (() => {
                      if (selected) return accentVariant.chip;
                      if (disabled) return "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed line-through";
                      return `bg-white text-slate-700 border-slate-200 ${accentVariant.chipHover}`;
                    })();
                    return (
                      <button
                        key={slot}
                        type="button"
                        disabled={disabled}
                        onClick={() => setTime(slot)}
                        className={`px-2 py-2.5 rounded-lg text-xs font-semibold border transition-colors ${slotClass}`}
                        data-testid={`${testidPrefix}-time-${slot.replace(":", "")}`}
                      >
                        {slot}
                      </button>
                    );
                  })}
                </div>
                {!date && (
                  <p className="mt-2 text-xs text-slate-500">{t.booking.pickDate}</p>
                )}

                <div className="mt-5">
                  <Label className="text-sm font-medium text-slate-700">
                    <MessageSquare className={`inline h-3.5 w-3.5 mr-1 ${accentVariant.iconColor}`} />
                    {t.booking.notes}
                  </Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={t.booking.notesPlaceholder}
                    rows={3}
                    className={`mt-1.5 bg-slate-50 border-slate-200 ${accentVariant.ring}`}
                    data-testid={`${testidPrefix}-input-notes`}
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className={`mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full ${accentVariant.btn} px-6 py-4 text-sm font-semibold text-white transition-colors shadow-sm disabled:opacity-60`}
              data-testid={`${testidPrefix}-submit`}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? t.booking.submitting : t.booking.submit}
            </button>
          </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default Booking;
